/**
 * Hook that returns a `KeyboardEvent` handler activating `handler` when the
 * user presses `Enter` or `Space` while a focusable container has focus.
 *
 * Background: chat bubbles render as `<div role="button" tabIndex={0}>`
 * rather than native `<button>` because they host inline `<a>` anchors
 * emitted by `linkifyText` — interactive descendants of `<button>` are
 * illegal per the HTML spec. Using `div + role="button"` keeps the markup
 * valid but drops native Enter / Space activation; this hook restores it.
 *
 * Calls `preventDefault` so Space does not trigger the browser's default
 * page-scroll behavior.
 *
 * Usage:
 *   const onKeyDown = useEnterOrSpaceActivation(handleTap);
 *   <div
 *     role="button"
 *     tabIndex={0}
 *     onClick={handleTap}
 *     onKeyDown={onKeyDown}
 *   />
 */

import { useCallback } from 'react';
import type React from 'react';

export function useEnterOrSpaceActivation<T extends HTMLElement>(
  handler: () => void,
): (event: React.KeyboardEvent<T>) => void {
  return useCallback(
    (event: React.KeyboardEvent<T>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handler();
      }
    },
    [handler],
  );
}
